// Isi Manual Penggunaan dashboard admin. Entri dengan `route` hanya tampil
// untuk peran yang boleh membuka route itu (meta.roles di router); entri
// tanpa route memakai `roles` (kosong = semua peran).
import { FINANCE_ROLES, ROLES } from "@/utils/permissions";

export interface ManualItem {
  title: string;
  description: string;
}

export interface ManualEntry {
  id: string;
  title: string;
  category: string;
  route?: string;
  roles?: string[];
  summary: string;
  tableColumns: ManualItem[];
  filters: ManualItem[];
  formFields: ManualItem[];
  buttons: ManualItem[];
  statusValues: ManualItem[];
  steps: ManualItem[];
}

export interface RoleGuide {
  label: string;
  summary: string;
  tasks: string[];
}

export const ROLE_GUIDES: Record<string, RoleGuide> = {
  [ROLES.Admin]: {
    label: "Admin",
    summary: "Akses penuh ke seluruh menu dashboard web, termasuk bantuan, keuangan, konten, komunikasi, merchandise, dan kemitraan.",
    tasks: [
      "Memantau ringkasan layanan di Dashboard.",
      "Meninjau dan memutuskan Pengajuan Bantuan serta data Orangtua Asuh.",
      "Mengawasi donasi, pembayaran, merchandise, dan transaksi.",
      "Mengelola konten kegiatan, kemitraan, template pesan, dan broadcast.",
    ],
  },
  [ROLES.PengurusBidang1]: {
    label: "Pengurus Bidang 1",
    summary: "Akses penuh yang sama dengan Admin pada dashboard web.",
    tasks: [
      "Meninjau dan memutuskan Pengajuan Bantuan serta data Orangtua Asuh.",
      "Memantau Dashboard, donasi, dan pembayaran.",
      "Membantu pengelolaan konten, kemitraan, dan komunikasi.",
    ],
  },
  [ROLES.Sekretariat]: {
    label: "Sekretariat",
    summary: "Mengelola data anggota, komunikasi (broadcast & template pesan), dan kemitraan.",
    tasks: [
      "Membaca data Pendataan Anggota dari form pendaftaran.",
      "Menyiapkan penerima dan jadwal Broadcast Pesan.",
      "Menyunting Template Pesan email dan WhatsApp.",
      "Mencatat Kemitraan dan Kegiatan Kemitraan.",
    ],
  },
  [ROLES.Bendahara]: {
    label: "Bendahara",
    summary: "Mengelola keuangan: donasi, pembayaran, dana bantuan, fakultas, merchandise, dan transaksi.",
    tasks: [
      "Memverifikasi konfirmasi transfer dan mencatat Donasi.",
      "Mengonfirmasi pembayaran manual dan memproses Transaksi Merchandise.",
      "Mengelola produk dan stok Merchandise.",
      "Mencatat Dana Bantuan yang disalurkan.",
      "Memantau Dashboard Pembayaran dan notifikasi pembayaran (ikon lonceng).",
    ],
  },
  [ROLES.PenulisKonten]: {
    label: "Penulis Konten",
    summary: "Menulis dan mempublikasikan kegiatan yang tampil di website. Setelah masuk, Anda langsung diarahkan ke menu Kegiatan.",
    tasks: [
      "Menulis kegiatan baru di Editor Kegiatan.",
      "Menyimpan draft, melihat preview, lalu mempublikasikan.",
      "Menarik kegiatan kembali ke draft bila perlu diperbaiki.",
    ],
  },
};

const GENERAL_DOCS: ManualEntry[] = [
  {
    id: "masuk-dan-peran",
    title: "Masuk dan Memilih Peran",
    category: "Umum",
    summary:
      "Dashboard admin memakai akun SSO IOM ITB. Satu akun bisa memiliki lebih dari satu peran; menu yang tampil di sidebar mengikuti peran yang sedang aktif.",
    tableColumns: [],
    filters: [],
    formFields: [],
    buttons: [
      { title: "Daftar Aplikasi", description: "Pilih Dashboard Admin (portal web IOM)." },
      { title: "Daftar Role", description: "Pilih peran yang ingin dipakai. Bila akun hanya punya satu peran, peran dipilih otomatis." },
      { title: "Masuk", description: "Masuk ke dashboard sesuai peran yang dipilih." },
      { title: "Logout", description: "Keluar dari sistem setelah konfirmasi." },
    ],
    statusValues: [],
    steps: [
      { title: "Buka dashboard admin", description: "Anda diarahkan ke halaman login SSO. Masukkan username/email dan password akun IOM ITB." },
      { title: "Pilih aplikasi", description: "Pada halaman Daftar Aplikasi, klik Dashboard Admin." },
      { title: "Pilih peran", description: "Klik salah satu peran pada Daftar Role, lalu klik Masuk." },
      { title: "Mulai bekerja", description: "Admin, Pengurus Bidang 1, Sekretariat, dan Bendahara masuk ke Dashboard; Penulis Konten masuk ke menu Kegiatan." },
    ],
  },
  {
    id: "navigasi-dan-akun",
    title: "Navigasi, Ganti Peran, dan Logout",
    category: "Umum",
    summary:
      "Sidebar kiri berisi menu yang boleh dibuka peran aktif, dikelompokkan per bidang. Header atas berisi nama peran, notifikasi (khusus peran keuangan), dan menu akun.",
    tableColumns: [
      { title: "Sidebar", description: "Daftar menu sesuai peran. Di layar kecil, buka dengan tombol menu (☰) di kiri atas." },
      { title: "Judul peran", description: "Di bawah logo, mis. Dashboard Bendahara atau Ruang Penulis Konten." },
      { title: "Menu akun", description: "Klik @username di kanan atas untuk Log out." },
    ],
    filters: [],
    formFields: [],
    buttons: [
      { title: "Ganti Peran", description: "Tombol Ganti Peran di halaman Manual Penggunaan ini, atau tombol Pilih Role di halaman Akses dibatasi, membuka kembali Daftar Role." },
      { title: "Log out", description: "Keluar dari akun SSO." },
    ],
    statusValues: [],
    steps: [
      { title: "Pindah menu", description: "Klik nama menu di sidebar." },
      { title: "Ganti peran", description: "Klik Ganti Peran, pilih peran lain, lalu klik Masuk." },
      { title: "Keluar", description: "Klik @username di kanan atas, lalu Log out. Selalu logout bila memakai komputer bersama." },
    ],
  },
  {
    id: "notifikasi-pembayaran",
    title: "Notifikasi Pembayaran",
    category: "Umum",
    roles: FINANCE_ROLES,
    summary:
      "Ikon lonceng di header menampilkan pembayaran Midtrans yang sudah lunas: donasi dan transaksi merchandise. Angka merah = jumlah notifikasi yang belum dibaca.",
    tableColumns: [
      { title: "Donasi Lunas", description: "Donasi yang dibayar lewat Midtrans." },
      { title: "Merchandise Lunas", description: "Pesanan merchandise yang dibayar lewat Midtrans. Pesanan dengan beberapa produk ditulis '… +N produk lain'." },
    ],
    filters: [],
    formFields: [],
    buttons: [
      { title: "Lonceng", description: "Membuka daftar notifikasi pembayaran." },
      { title: "Muat ulang", description: "Mengambil notifikasi terbaru." },
      { title: "Dashboard Pembayaran / Transaksi Merchandise", description: "Pintasan ke menu terkait." },
    ],
    statusValues: [],
    steps: [
      { title: "Cek lonceng", description: "Klik ikon lonceng saat ada angka merah." },
      { title: "Tindak lanjuti", description: "Klik pintasan Transaksi Merchandise untuk memproses pengiriman pesanan yang sudah lunas." },
      { title: "Bila muncul pesan sesi berakhir", description: "Muat ulang halaman (F5) atau login kembali." },
    ],
  },
  {
    id: "akses-dibatasi",
    title: "Halaman Akses Dibatasi",
    category: "Umum",
    summary:
      "Halaman 'Akses dibatasi' muncul bila Anda membuka menu yang tidak termasuk hak akses peran aktif (mis. dari link yang dibagikan rekan).",
    tableColumns: [],
    filters: [],
    formFields: [],
    buttons: [
      { title: "Pilih Role", description: "Kembali ke Daftar Role untuk memilih peran lain yang memiliki akses." },
      { title: "Dashboard", description: "Kembali ke halaman utama peran aktif." },
    ],
    statusValues: [],
    steps: [
      { title: "Periksa peran aktif", description: "Lihat bagian Peran Anda di Manual Penggunaan untuk daftar menu yang bisa dibuka." },
      { title: "Ganti peran bila punya", description: "Klik Pilih Role dan pilih peran yang sesuai." },
      { title: "Minta akses", description: "Bila akun Anda tidak memiliki peran tersebut, hubungi Admin untuk menambahkan peran di SSO." },
    ],
  },
];

const FEATURE_DOCS: ManualEntry[] = [
  {
    id: "dashboard",
    title: "Dashboard",
    category: "Dashboard",
    route: "/dashboard",
    summary:
      "Dashboard adalah halaman utama untuk memantau ringkasan layanan IOM ITB, status pengajuan bantuan, progres donasi, pesanan merchandise, dan aktivitas terbaru.",
    tableColumns: [
      { title: "Kartu Pengajuan Perlu Proses", description: "Menampilkan total pengajuan yang belum selesai, dengan breakdown Bantuan IOM, Bantuan Kesehatan atau Bankes, dan Orang Tua Asuh atau OTA." },
      { title: "Kartu Bantuan Disetujui Bulan Ini", description: "Menampilkan jumlah bantuan yang disetujui pada bulan berjalan, dengan breakdown IOM, Bankes, dan OTA." },
      { title: "Kartu Pesanan Merchandise Baru", description: "Menampilkan jumlah pesanan merchandise baru yang perlu diperiksa." },
      { title: "Kartu Donasi Terkumpul", description: "Menampilkan total donasi, dengan breakdown Donasi IOM dan Orang Tua Asuh." },
      { title: "Kartu Total Anggota", description: "Menampilkan total anggota yang tercatat pada sistem." },
      { title: "Tabel Pengajuan Terbaru", description: "Menampilkan Nama Pemohon, NIM, dan Status pengajuan." },
      { title: "Tabel Aktivitas Admin", description: "Menampilkan Admin, Waktu, dan Perubahan Status." },
    ],
    filters: [],
    formFields: [],
    buttons: [
      { title: "Refresh Data", description: "Memuat ulang statistik, grafik, data terbaru, dan aktivitas admin dari backend." },
      { title: "Dropdown pada kartu Pengajuan Perlu Proses", description: "Membuka rincian jumlah pengajuan untuk Bantuan IOM, Bankes, dan OTA." },
      { title: "Dropdown pada kartu Bantuan Disetujui", description: "Membuka rincian jumlah bantuan disetujui untuk IOM, Bankes, dan OTA." },
    ],
    statusValues: [
      { title: "Status Tidak Diketahui", description: "Status awal ketika pengajuan belum memiliki status proses yang jelas." },
      { title: "Verifikasi Berkas", description: "Pengajuan sedang diperiksa kelengkapan berkasnya." },
      { title: "Wawancara", description: "Pemohon masuk tahap wawancara." },
      { title: "Menunggu", description: "Pengajuan sedang menunggu tindak lanjut." },
      { title: "Diproses", description: "Pengajuan sedang diproses admin." },
      { title: "Disetujui", description: "Pengajuan bantuan disetujui." },
      { title: "Ditolak", description: "Pengajuan bantuan ditolak." },
    ],
    steps: [
      { title: "Buka Dashboard", description: "Klik menu Dashboard pada sidebar." },
      { title: "Periksa kartu ringkasan", description: "Lihat jumlah pengajuan perlu proses, bantuan disetujui bulan ini, pesanan merchandise baru, donasi terkumpul, dan total anggota." },
      { title: "Buka breakdown kartu", description: "Klik kartu yang memiliki ikon panah untuk melihat rincian IOM, Bankes, atau OTA." },
      { title: "Periksa tabel terbaru", description: "Gunakan tabel pengajuan terbaru dan aktivitas admin untuk menentukan data yang perlu ditindaklanjuti." },
      { title: "Klik Refresh Data", description: "Gunakan tombol Refresh Data setelah ada perubahan data di menu lain." },
    ],
  },
  {
    id: "dashboard-pembayaran",
    title: "Dashboard Pembayaran",
    category: "Keuangan",
    route: "/dashboard-pembayaran",
    summary:
      "Dashboard Pembayaran digunakan untuk memantau pembayaran iuran sukarela dan kontribusi sukarela berdasarkan periode, jenis pembayaran, status pembayaran, metode pembayaran, fakultas, dan tren harian.",
    tableColumns: [
      { title: "Tanggal", description: "Tanggal pembayaran, diambil dari paidAt, date, atau createdAt." },
      { title: "Nama", description: "Nama pembayar atau donatur." },
      { title: "Jenis", description: "Jenis pembayaran, seperti iuran_sukarela atau kontribusi_sukarela." },
      { title: "Fakultas", description: "Nama fakultas yang terkait dengan pembayaran." },
      { title: "Nominal", description: "Nominal pembayaran dalam format rupiah." },
      { title: "Metode", description: "Metode pembayaran: manual atau midtrans." },
      { title: "Status", description: "Status pembayaran: pending, settlement, expired, failed, atau refunded." },
    ],
    filters: [
      { title: "Mulai", description: "Tanggal awal periode pembayaran." },
      { title: "Sampai", description: "Tanggal akhir periode pembayaran." },
      { title: "Jenis", description: "Pilihan: Semua, Iuran Sukarela, atau Kontribusi Sukarela." },
      { title: "Status", description: "Pilihan: Semua, Pending, Settlement, Expired, Failed, atau Refunded." },
    ],
    formFields: [],
    buttons: [
      { title: "Terapkan", description: "Menerapkan filter tanggal, jenis pembayaran, dan status pembayaran." },
    ],
    statusValues: [
      { title: "pending", description: "Pembayaran dibuat tetapi belum lunas." },
      { title: "settlement", description: "Pembayaran berhasil atau lunas." },
      { title: "expired", description: "Pembayaran melewati batas waktu." },
      { title: "failed", description: "Pembayaran gagal." },
      { title: "refunded", description: "Pembayaran dikembalikan." },
      { title: "manual", description: "Pembayaran dicatat manual oleh admin." },
      { title: "midtrans", description: "Pembayaran diproses melalui Midtrans." },
    ],
    steps: [
      { title: "Buka Dashboard Pembayaran", description: "Klik menu Dashboard Pembayaran pada sidebar." },
      { title: "Atur periode", description: "Isi tanggal Mulai dan Sampai untuk menentukan periode laporan." },
      { title: "Pilih jenis pembayaran", description: "Pilih Semua, Iuran Sukarela, atau Kontribusi Sukarela." },
      { title: "Pilih status pembayaran", description: "Pilih Pending, Settlement, Expired, Failed, Refunded, atau Semua." },
      { title: "Klik Terapkan", description: "Sistem akan memuat ulang ringkasan KPI, status pembayaran, jenis pembayaran, metode pembayaran, tren harian, dan tabel pembayaran terbaru." },
    ],
  },
  {
    id: "kegiatan",
    title: "Kegiatan",
    category: "Konten",
    route: "/kegiatan",
    summary:
      "Menu Kegiatan digunakan untuk mengelola publikasi kegiatan IOM ITB. Halaman ini menampilkan daftar kegiatan, filter status, pencarian, tombol buat kegiatan, editor, preview, publish, tarik ke draft, dan hapus.",
    tableColumns: [
      { title: "Judul", description: "Judul kegiatan yang ditampilkan pada kartu kegiatan." },
      { title: "Deskripsi", description: "Ringkasan konten kegiatan." },
      { title: "Status", description: "Draft atau Published." },
      { title: "Tanggal Status", description: "Jika Published menampilkan tanggal publikasi; jika Draft menampilkan tanggal pembaruan." },
      { title: "Gambar", description: "Thumbnail kegiatan." },
    ],
    filters: [
      { title: "Filter Semua", description: "Menampilkan kegiatan Draft dan Published." },
      { title: "Filter Draft", description: "Menampilkan kegiatan yang masih berstatus draft." },
      { title: "Filter Published", description: "Menampilkan kegiatan yang sudah dipublikasikan." },
      { title: "Pencarian", description: "Mencari kegiatan berdasarkan judul, tag kategori, atau konten." },
    ],
    formFields: [
      { title: "Judul Kegiatan", description: "Diisi pada halaman editor sebagai judul utama kegiatan." },
      { title: "Deskripsi/Konten", description: "Diisi melalui rich text editor pada halaman editor." },
      { title: "Media", description: "Berisi gambar upload, URL gambar, atau URL YouTube." },
      { title: "Caption Media", description: "Keterangan untuk media yang sedang dipilih." },
      { title: "Thumbnail", description: "Dipilih dari media gambar dengan tombol Set as thumbnail." },
    ],
    buttons: [
      { title: "Tulis Kegiatan", description: "Membuka editor kegiatan baru." },
      { title: "Edit", description: "Membuka editor kegiatan berdasarkan id kegiatan." },
      { title: "Publish", description: "Mengubah status kegiatan dari draft menjadi published." },
      { title: "Tarik ke Draft", description: "Mengubah status kegiatan dari published kembali ke draft." },
      { title: "Preview", description: "Membuka halaman preview kegiatan." },
      { title: "Hapus", description: "Menghapus kegiatan setelah konfirmasi." },
      { title: "Prev / Next", description: "Berpindah halaman daftar kegiatan." },
    ],
    statusValues: [
      { title: "draft", description: "Kegiatan belum dipublikasikan dan masih dapat diedit." },
      { title: "published", description: "Kegiatan sudah dipublikasikan." },
    ],
    steps: [
      { title: "Buka menu Kegiatan", description: "Klik menu Kegiatan pada sidebar." },
      { title: "Cari atau filter kegiatan", description: "Gunakan filter Semua, Draft, Published, atau kolom pencarian." },
      { title: "Buat kegiatan baru", description: "Klik Tulis Kegiatan untuk membuka editor kegiatan baru." },
      { title: "Edit kegiatan lama", description: "Klik Edit pada kartu kegiatan yang ingin diubah." },
      { title: "Publish atau tarik ke draft", description: "Gunakan Publish untuk mempublikasikan; gunakan Tarik ke Draft untuk menyembunyikan kembali dari status published." },
      { title: "Hapus kegiatan", description: "Klik Hapus, lalu konfirmasi penghapusan." },
    ],
  },
  {
    id: "editor-kegiatan",
    title: "Editor Kegiatan",
    category: "Konten",
    route: "/kegiatan/new/edit",
    summary:
      "Editor Kegiatan digunakan untuk membuat dan menyunting konten kegiatan secara lengkap, termasuk judul, konten rich text, media gambar, URL gambar, YouTube, caption, urutan media, thumbnail, draft, preview, dan publish.",
    tableColumns: [],
    filters: [],
    formFields: [
      { title: "Judul kegiatan", description: "Input utama dengan placeholder Judul kegiatan." },
      { title: "Deskripsi/konten kegiatan", description: "Rich text editor untuk menulis isi kegiatan dalam format HTML." },
      { title: "Tipe Media", description: "Pilihan upload, url, atau youtube." },
      { title: "Upload gambar", description: "Input file image dengan multiple upload." },
      { title: "URL Gambar", description: "Input URL gambar dengan placeholder https://." },
      { title: "URL YouTube", description: "Input URL YouTube dengan placeholder https://youtube.com/watch?v=." },
      { title: "Caption", description: "Input keterangan untuk media yang sedang aktif." },
      { title: "Thumbnail", description: "Gambar utama kegiatan yang wajib ada sebelum publish." },
    ],
    buttons: [
      { title: "Kembali", description: "Kembali dari editor ke halaman sebelumnya." },
      { title: "Simpan Draft", description: "Menyimpan kegiatan sebagai draft." },
      { title: "Tarik ke Draft", description: "Mengubah kegiatan published menjadi draft." },
      { title: "Preview", description: "Membuka tampilan preview kegiatan." },
      { title: "Publish", description: "Mempublikasikan kegiatan. Judul dan thumbnail wajib diisi." },
      { title: "Panah kiri / kanan media", description: "Berpindah ke media sebelumnya atau berikutnya." },
      { title: "Set as thumbnail", description: "Menjadikan gambar yang sedang dipilih sebagai thumbnail." },
      { title: "Pindah kiri / kanan", description: "Mengubah urutan media." },
      { title: "Hapus media", description: "Menghapus media yang sedang dipilih." },
      { title: "Tambah Media", description: "Menambahkan URL gambar atau URL YouTube ke daftar media." },
    ],
    statusValues: [
      { title: "upload", description: "Menambahkan media dari file gambar lokal." },
      { title: "url", description: "Menambahkan media dari URL gambar." },
      { title: "youtube", description: "Menambahkan media dari URL YouTube." },
      { title: "draft", description: "Konten belum dipublikasikan." },
      { title: "published", description: "Konten sudah dipublikasikan." },
    ],
    steps: [
      { title: "Isi judul", description: "Tulis judul kegiatan pada input Judul kegiatan." },
      { title: "Tulis konten", description: "Tulis isi kegiatan pada rich text editor." },
      { title: "Tambahkan media", description: "Pilih tipe media upload, url, atau youtube, lalu masukkan file atau URL sesuai pilihan." },
      { title: "Atur caption dan urutan media", description: "Pilih media, isi Caption, lalu gunakan tombol pindah kiri atau kanan untuk mengatur urutan." },
      { title: "Pilih thumbnail", description: "Pilih media gambar dan klik Set as thumbnail." },
      { title: "Simpan atau publish", description: "Klik Simpan Draft untuk menyimpan sementara, Preview untuk melihat tampilan, atau Publish untuk mempublikasikan." },
    ],
  },
  {
    id: "merchandise",
    title: "Merchandise",
    category: "Produk",
    route: "/merchandise",
    summary:
      "Menu Merchandise digunakan untuk mengelola produk yang tampil di katalog publik: nama, kategori, stok, harga jual, HPP, berat untuk ongkir, opsi produk (warna/lengan/size dengan tambahan harga), deskripsi, link pembelian, dan gambar.",
    tableColumns: [
      { title: "Kartu ringkasan", description: "Total Produk, Stok Rendah, dan Stok Habis." },
      { title: "Produk", description: "Gambar, nama produk, dan tanggal dibuat." },
      { title: "Harga", description: "Harga jual dasar (belum termasuk tambahan harga opsi)." },
      { title: "Stok", description: "Jumlah stok tersisa. Stok berkurang otomatis saat pesanan dibuat dan kembali bila pesanan dibatalkan/kedaluwarsa." },
      { title: "Kategori", description: "Kategori produk, atau 'Tanpa kategori'." },
      { title: "Deskripsi / Link", description: "Ringkasan produk dan tombol Lihat untuk membuka link pembelian." },
      { title: "Diperbarui", description: "Tanggal data terakhir diubah." },
      { title: "Aksi", description: "Tombol Edit dan Hapus." },
    ],
    filters: [
      { title: "Per Halaman", description: "Jumlah produk per halaman." },
      { title: "Cari", description: "Cari berdasarkan nama merchandise." },
    ],
    formFields: [
      { title: "Nama Produk", description: "Nama yang tampil di katalog. Wajib." },
      { title: "Stok & Harga", description: "Stok, harga jual, dan HPP (harga pokok, hanya untuk laporan internal di Dashboard Merchandise)." },
      { title: "Berat (gram)", description: "Berat per pcs termasuk kemasan, 1–30.000 gram. Dipakai menghitung ongkir. Kosong = dianggap 500 gram." },
      { title: "Kategori", description: "Pilih kategori yang ada atau ketik nama kategori baru." },
      { title: "Opsi Produk", description: "Pilihan yang wajib dipilih pembeli, mis. Warna, Lengan, Size. Tiap pilihan boleh punya tambahan harga (mis. XXL +Rp15.000). Tersedia Template Pakaian dan Template Warna." },
      { title: "Deskripsi", description: "Ringkasan produk untuk halaman publik." },
      { title: "Link Pembelian", description: "Link eksternal (mis. ITB Press) bila ada." },
      { title: "Gambar Produk", description: "URL gambar atau unggah JPG/PNG maksimal 5 MB." },
    ],
    buttons: [
      { title: "Tambah Merchandise", description: "Membuka form produk baru." },
      { title: "Kelola Kategori", description: "Tambah, ubah, atau hapus kategori. Mengubah kategori memperbarui semua produk terkait; menghapus kategori tidak menghapus produk." },
      { title: "Edit", description: "Membuka form produk yang dipilih." },
      { title: "Hapus", description: "Menghapus produk setelah konfirmasi." },
      { title: "Simpan / Batal", description: "Menyimpan atau menutup form tanpa menyimpan." },
    ],
    statusValues: [
      { title: "Stok Rendah", description: "Produk dengan stok tinggal sedikit — segera tambah stok." },
      { title: "Stok Habis", description: "Stok 0; pembeli tidak bisa memesan produk ini." },
    ],
    steps: [
      { title: "Buka menu Merchandise", description: "Klik Merchandise pada sidebar (grup Katalog & Transaksi)." },
      { title: "Tambah produk", description: "Klik Tambah Merchandise, isi Nama, Stok, Harga, HPP, Berat, Kategori, Deskripsi, dan Gambar." },
      { title: "Atur opsi bila perlu", description: "Pada Opsi Produk klik + Tambah opsi (atau pakai template), isi pilihan dan tambahan harga bila ada. Pembeli wajib memilih semua opsi." },
      { title: "Isi berat dengan benar", description: "Berat menentukan ongkir pesanan. Timbang produk beserta kemasannya." },
      { title: "Simpan", description: "Klik Simpan. Produk langsung tampil di katalog publik." },
      { title: "Perbarui stok", description: "Klik Edit pada produk saat ada stok masuk, ubah Stok, lalu Simpan." },
    ],
  },
  {
    id: "transaksi-merchandise",
    title: "Transaksi Merchandise",
    category: "Produk",
    route: "/transactions",
    summary:
      "Menu Transaksi Merchandise digunakan untuk memeriksa pesanan (bisa berisi beberapa produk dari keranjang), memverifikasi bukti transfer, mengonfirmasi pembayaran manual, mengubah status pengiriman, menyalin link status pesanan, dan mengekspor data.",
    tableColumns: [
      { title: "Kartu ringkasan", description: "Total Transaksi, Lunas, dan Perlu Diproses." },
      { title: "Pesanan", description: "Kode pesanan dan daftar produk (nama, opsi, qty). Pesanan keranjang menampilkan semua produk." },
      { title: "Pembeli", description: "Nama, email, dan nomor telepon pembeli." },
      { title: "Alamat / Catatan", description: "Alamat kirim dan catatan pembeli." },
      { title: "Total", description: "Total bayar, jumlah pcs, dan ongkir beserta berat ditagih (kg). Arahkan kursor ke baris ongkir untuk melihat layanan kurir, sumber tarif (RajaOngkir/AgenWebsite/Tabel zona), dan kode pos." },
      { title: "Pembayaran", description: "Metode (Manual/Midtrans) dan status bayar." },
      { title: "Status pesanan", description: "Dropdown status pengiriman beserta tombol Simpan." },
      { title: "Bukti", description: "Tombol Lihat untuk bukti transfer manual; MID untuk Midtrans." },
    ],
    filters: [
      { title: "Metode Bayar", description: "Semua, Manual, atau Midtrans." },
      { title: "Status Bayar", description: "Semua, Menunggu, Lunas, Kedaluwarsa, Gagal, Dikembalikan." },
      { title: "Status Pesanan", description: "Semua, Menunggu, Diproses, Dikirim, Tiba, Selesai, Dibatalkan, Ditolak." },
      { title: "Cari", description: "Kode, nama, email, atau nama produk." },
      { title: "Per halaman", description: "5, 10, 20, atau 50 baris." },
    ],
    formFields: [
      { title: "Status pesanan", description: "Pilih status baru dari dropdown pada baris pesanan, lalu klik Simpan." },
    ],
    buttons: [
      { title: "Konfirmasi Pembayaran", description: "Muncul untuk pesanan manual yang masih Menunggu. Klik setelah bukti transfer dicocokkan dengan mutasi rekening; pembeli menerima notifikasi Email dan WhatsApp." },
      { title: "Simpan", description: "Menyimpan perubahan status pesanan." },
      { title: "Salin Link", description: "Menyalin link halaman status pesanan untuk dikirim ke pembeli." },
      { title: "Hapus", description: "Menghapus pesanan setelah konfirmasi; stok produk dikembalikan." },
      { title: "Export CSV / Export Excel", description: "Mengunduh semua transaksi yang cocok dengan filter (bukan hanya halaman ini). Satu baris per produk; ongkir, berat, sumber ongkir, dan kode pos ada di baris pertama tiap pesanan." },
    ],
    statusValues: [
      { title: "Menunggu (waiting)", description: "Pesanan baru, belum diproses." },
      { title: "Diproses (on process)", description: "Pesanan sedang disiapkan." },
      { title: "Dikirim (on delivery)", description: "Pesanan dikirim. Pembeli mendapat notifikasi Email & WhatsApp." },
      { title: "Tiba (arrived)", description: "Pesanan sampai. Pembeli mendapat notifikasi." },
      { title: "Selesai (done)", description: "Pesanan selesai. Pembeli mendapat notifikasi." },
      { title: "Dibatalkan / Ditolak", description: "Pesanan dibatalkan atau ditolak; stok semua produk dalam pesanan dikembalikan." },
      { title: "Status bayar", description: "Menunggu (pending), Lunas (settlement), Kedaluwarsa (expired), Gagal (failed), Dikembalikan (refunded)." },
    ],
    steps: [
      { title: "Buka menu Transaksi Merchandise", description: "Klik Transaksi Merchandise pada sidebar." },
      { title: "Saring pesanan baru", description: "Pilih Status Bayar = Menunggu atau Status Pesanan = Menunggu." },
      { title: "Periksa bukti transfer", description: "Klik Lihat pada kolom Bukti, cocokkan nominal dengan Total (sudah termasuk ongkir) dan mutasi rekening." },
      { title: "Konfirmasi pembayaran", description: "Klik Konfirmasi Pembayaran lalu setujui. Status bayar menjadi Lunas dan pembeli diberi tahu." },
      { title: "Kirim barang", description: "Kirim ke alamat pada kolom Alamat dengan layanan kurir yang tertera di tooltip ongkir." },
      { title: "Perbarui status", description: "Ubah status menjadi Dikirim, lalu Tiba/Selesai, dan klik Simpan setiap kali." },
      { title: "Ekspor laporan", description: "Atur filter, lalu klik Export Excel atau Export CSV." },
    ],
  },
  {
    id: "donasi",
    title: "Donasi",
    category: "Keuangan",
    route: "/donasi",
    summary:
      "Menu Donasi berisi dua tab: Konfirmasi Transfer (Form) — kiriman donatur dari form donasi website yang belum diverifikasi — dan Catatan Donasi — data donasi resmi yang dicatat admin atau tercatat otomatis dari Midtrans.",
    tableColumns: [
      { title: "Tab Konfirmasi Transfer (Form)", description: "Dikirim, Donatur, Jenis Kontribusi, Fakultas/Sekolah, Nominal, Bukti Bayar, dan Tanda Terima." },
      { title: "Tab Catatan Donasi", description: "No, Tanggal, Donatur (nama, email, WA), Jenis, Fakultas, Kode, Nominal, Metode, Status, Bukti Manual, dan Aksi." },
    ],
    filters: [
      { title: "Per halaman", description: "5, 10, 20, atau 50 data per halaman." },
      { title: "Metode", description: "Semua, Manual, atau Midtrans." },
      { title: "Status", description: "Semua, Pending, Settlement, Expired, Failed, atau Refunded." },
      { title: "Jenis Donasi", description: "Iuran Sukarela, Kontribusi Anggota, Kontribusi Donatur, Pembelian Merchandise, Kontribusi Sukarela." },
      { title: "Cari Nama / Cari", description: "Cari nama donatur; di tab Konfirmasi Transfer juga email dan nomor." },
    ],
    formFields: [
      { title: "Nama", description: "Nama donatur. Wajib diisi." },
      { title: "Email / No WA", description: "Kontak donatur untuk notifikasi." },
      { title: "Jenis Donasi", description: "iuran_sukarela, kontribusi_anggota, kontribusi_donatur, pembelian_merchandise, kontribusi_sukarela." },
      { title: "Fakultas", description: "Jika dipilih, nominal akhir ditambah kode unik fakultas." },
      { title: "Uang yang didonasi / Tanggal uang masuk", description: "Nominal dan tanggal donasi masuk. Wajib diisi." },
      { title: "Bank", description: "Bank tujuan/asal transfer." },
      { title: "Notifikasi melalui?", description: "WhatsApp dan/atau Email. Wajib dipilih." },
      { title: "Sembunyikan nama? / Hamba Allah?", description: "Menyamarkan nama donatur di tampilan publik." },
      { title: "Bukti Transfer Manual", description: "Unggah gambar bukti bayar manual bila ada." },
    ],
    buttons: [
      { title: "Tambah Donasi", description: "Membuka form donasi manual." },
      { title: "Excel", description: "Mengunduh daftar donasi sesuai filter." },
      { title: "Minta via WA", description: "Di tab Konfirmasi Transfer: membuka WhatsApp donatur untuk meminta bukti/tanda terima." },
      { title: "Lihat", description: "Membuka bukti bayar manual." },
      { title: "Edit", description: "Mengubah data donasi." },
      { title: "Simpan / Batal", description: "Menyimpan atau menutup form." },
    ],
    statusValues: [
      { title: "pending", description: "Pembayaran belum lunas." },
      { title: "settlement", description: "Pembayaran berhasil/lunas." },
      { title: "expired / failed / refunded", description: "Kedaluwarsa, gagal, atau dikembalikan." },
      { title: "manual / midtrans", description: "Dicatat manual oleh admin atau dibayar lewat Midtrans (otomatis)." },
    ],
    steps: [
      { title: "Buka menu Donasi", description: "Klik Donasi pada sidebar." },
      { title: "Periksa konfirmasi transfer", description: "Di tab Konfirmasi Transfer (Form), buka Bukti Bayar dan cocokkan dengan mutasi rekening." },
      { title: "Catat donasi", description: "Pindah ke tab Catatan Donasi, klik Tambah Donasi, isi data sesuai transfer yang valid, lalu Simpan. Donatur menerima notifikasi sesuai pilihan." },
      { title: "Minta kelengkapan", description: "Bila bukti kurang jelas, klik Minta via WA." },
      { title: "Ekspor laporan", description: "Atur filter lalu klik Excel." },
    ],
  },
  {
    id: "fakultas",
    title: "Fakultas",
    category: "Master Data",
    route: "/fakultas",
    summary:
      "Menu Fakultas digunakan untuk mengelola master data fakultas dan kode unik 3 digit yang dipakai pada pembayaran donasi/iuran manual.",
    tableColumns: [
      { title: "Nama", description: "Nama fakultas." },
      { title: "Kode Unik", description: "Kode unik 3 digit." },
      { title: "Status", description: "Aktif atau Nonaktif." },
      { title: "Aksi", description: "Tombol Edit dan Hapus." },
    ],
    filters: [],
    formFields: [
      { title: "Nama Fakultas", description: "Nama fakultas, contoh FTI. Wajib diisi." },
      { title: "Kode Unik (3 digit)", description: "Kode unik maksimal 3 digit, contoh 001. Wajib diisi." },
      { title: "Aktif", description: "Checkbox untuk menentukan fakultas aktif atau nonaktif." },
    ],
    buttons: [
      { title: "+ Tambah Fakultas", description: "Membuka form tambah fakultas." },
      { title: "Edit", description: "Membuka form edit fakultas." },
      { title: "Hapus", description: "Menghapus fakultas setelah konfirmasi." },
      { title: "Batal", description: "Menutup form tanpa menyimpan." },
      { title: "Simpan", description: "Menyimpan data fakultas." },
    ],
    statusValues: [
      { title: "Aktif", description: "Fakultas dapat digunakan pada fitur pembayaran/donasi." },
      { title: "Nonaktif", description: "Fakultas tidak aktif." },
    ],
    steps: [
      { title: "Buka menu Fakultas", description: "Klik menu Fakultas pada sidebar." },
      { title: "Tambah fakultas", description: "Klik + Tambah Fakultas, isi Nama Fakultas, Kode Unik 3 digit, dan status Aktif." },
      { title: "Simpan data", description: "Klik Simpan untuk menyimpan data." },
      { title: "Edit data", description: "Klik Edit pada baris fakultas yang ingin diubah." },
      { title: "Hapus data", description: "Klik Hapus pada baris fakultas yang ingin dihapus." },
    ],
  },
  {
    id: "pengajuan-bantuan",
    title: "Pengajuan Bantuan",
    category: "Bantuan",
    route: "/pengajuan-bantuan",
    summary:
      "Menu Pengajuan Bantuan digunakan untuk meninjau submission bantuan dari Tally, membuka detail jawaban, mengubah status pengajuan, menambahkan catatan, dan melihat riwayat perubahan status.",
    tableColumns: [
      { title: "Tanggal Submit", description: "Tanggal submission masuk." },
      { title: "Kolom dari answersByLabel", description: "Kolom utama diambil dari label pertanyaan form Tally yang dikirim backend melalui payload answersByLabel." },
      { title: "Status", description: "Status proses pengajuan." },
      { title: "Last Update", description: "Tanggal perubahan terakhir." },
      { title: "Aksi", description: "Tombol Detail." },
    ],
    filters: [
      { title: "Pencarian", description: "Mencari data berdasarkan nilai di answersByLabel, misalnya nama atau NIM jika label tersebut ada pada form Tally." },
      { title: "Pagination", description: "Tombol Sebelumnya dan Berikutnya untuk berpindah halaman." },
    ],
    formFields: [
      { title: "Status", description: "Select status pengajuan pada modal detail." },
      { title: "Catatan", description: "Textarea dengan placeholder Tambahkan catatan (opsional)." },
      { title: "Label Pertanyaan dan Jawaban", description: "Modal detail menampilkan seluruh pasangan label dan jawaban dari answersByLabel." },
    ],
    buttons: [
      { title: "Detail", description: "Membuka modal Detail Pengajuan." },
      { title: "Simpan Perubahan", description: "Menyimpan perubahan status dan catatan." },
      { title: "Ya, Simpan", description: "Konfirmasi untuk menyimpan perubahan status." },
      { title: "Batal", description: "Membatalkan konfirmasi simpan." },
      { title: "Tutup", description: "Menutup modal detail." },
      { title: "Ya, Tutup", description: "Konfirmasi menutup modal ketika ada perubahan yang belum disimpan." },
      { title: "Tetap di sini", description: "Membatalkan penutupan modal dan tetap di modal detail." },
    ],
    statusValues: [
      { title: "TIDAK_DIKETAHUI", description: "Ditampilkan sebagai Status Tidak Diketahui." },
      { title: "VERIFIKASI_BERKAS", description: "Ditampilkan sebagai Verifikasi Berkas." },
      { title: "DIPANGGIL_WAWANCARA", description: "Ditampilkan sebagai Dipanggil Wawancara." },
      { title: "KEPUTUSAN_DITERIMA", description: "Ditampilkan sebagai Keputusan Diterima." },
      { title: "KEPUTUSAN_DITOLAK", description: "Ditampilkan sebagai Keputusan Ditolak." },
    ],
    steps: [
      { title: "Buka menu Pengajuan Bantuan", description: "Klik menu Pengajuan Bantuan pada sidebar." },
      { title: "Cari data pengajuan", description: "Gunakan kolom pencarian untuk menemukan submission berdasarkan nilai jawaban form Tally." },
      { title: "Buka detail", description: "Klik Detail untuk melihat seluruh label pertanyaan dan jawaban dari answersByLabel." },
      { title: "Ubah status", description: "Pilih salah satu status: Status Tidak Diketahui, Verifikasi Berkas, Dipanggil Wawancara, Keputusan Diterima, atau Keputusan Ditolak." },
      { title: "Isi catatan", description: "Isi Catatan jika perlu menjelaskan keputusan atau proses admin." },
      { title: "Simpan perubahan", description: "Klik Simpan Perubahan, lalu konfirmasi dengan Ya, Simpan." },
      { title: "Periksa riwayat", description: "Baca riwayat perubahan status pada modal detail untuk melihat waktu, keterangan, dan aktor perubahan." },
    ],
  },
  {
    id: "pendataan-anggota",
    title: "Pendataan Anggota",
    category: "Data Anggota",
    route: "/pendataan-anggota",
    summary:
      "Menu Pendataan Anggota digunakan untuk membaca submission pendaftaran anggota dari Tally. Halaman ini bersifat read-only pada frontend: admin dapat mencari, melihat tabel, dan berpindah halaman.",
    tableColumns: [
      { title: "No", description: "Nomor urut data pada halaman aktif." },
      { title: "Tanggal Submit", description: "Tanggal submission masuk." },
      { title: "Kolom dari answersByLabel", description: "Kolom dinamis dari label pertanyaan form Tally untuk pendataan anggota." },
    ],
    filters: [
      { title: "Pencarian", description: "Mencari nilai jawaban dari form Tally." },
      { title: "Pagination", description: "Tombol Sebelumnya dan Berikutnya untuk berpindah halaman." },
    ],
    formFields: [],
    buttons: [
      { title: "Sebelumnya", description: "Berpindah ke halaman sebelumnya." },
      { title: "Berikutnya", description: "Berpindah ke halaman berikutnya." },
    ],
    statusValues: [],
    steps: [
      { title: "Buka menu Pendataan Anggota", description: "Klik menu Pendataan Anggota pada sidebar." },
      { title: "Cari submission", description: "Gunakan kolom pencarian untuk menemukan data berdasarkan jawaban form." },
      { title: "Baca data tabel", description: "Periksa Tanggal Submit dan kolom jawaban yang ditampilkan dari answersByLabel." },
      { title: "Pindah halaman", description: "Gunakan tombol Sebelumnya atau Berikutnya." },
    ],
  },
  {
    id: "orangtua-asuh",
    title: "Orangtua Asuh",
    category: "Bantuan",
    route: "/orangtua-asuh",
    summary:
      "Menu Orangtua Asuh digunakan untuk membaca submission program Orang Tua Asuh dari Tally. Halaman ini bersifat read-only pada frontend: admin dapat mencari, melihat tabel, dan berpindah halaman.",
    tableColumns: [
      { title: "No", description: "Nomor urut data pada halaman aktif." },
      { title: "Tanggal Submit", description: "Tanggal submission masuk." },
      { title: "Kolom dari answersByLabel", description: "Kolom dinamis dari label pertanyaan form Tally untuk program Orang Tua Asuh." },
    ],
    filters: [
      { title: "Pencarian", description: "Mencari nilai jawaban dari form Tally." },
      { title: "Pagination", description: "Tombol Sebelumnya dan Berikutnya untuk berpindah halaman." },
    ],
    formFields: [],
    buttons: [
      { title: "Sebelumnya", description: "Berpindah ke halaman sebelumnya." },
      { title: "Berikutnya", description: "Berpindah ke halaman berikutnya." },
    ],
    statusValues: [],
    steps: [
      { title: "Buka menu Orangtua Asuh", description: "Klik menu Orangtua Asuh pada sidebar." },
      { title: "Cari submission", description: "Gunakan kolom pencarian untuk menemukan data berdasarkan jawaban form." },
      { title: "Baca data tabel", description: "Periksa Tanggal Submit dan kolom jawaban yang ditampilkan dari answersByLabel." },
      { title: "Pindah halaman", description: "Gunakan tombol Sebelumnya atau Berikutnya." },
    ],
  },
  {
    id: "dana-bantuan",
    title: "Dana Bantuan",
    category: "Bantuan",
    route: "/dana-bantuan",
    summary:
      "Menu Dana Bantuan digunakan untuk mengelola catatan bantuan berdasarkan nama, NIM, program studi, jenis bantuan, bulan, tahun, dan jumlah donasi.",
    tableColumns: [
      { title: "Nama", description: "Nama penerima bantuan." },
      { title: "NIM", description: "NIM penerima bantuan." },
      { title: "Program Studi", description: "Program studi penerima bantuan." },
      { title: "Jenis Bantuan", description: "Jenis bantuan yang diberikan." },
      { title: "Bulan", description: "Bulan bantuan." },
      { title: "Tahun", description: "Tahun bantuan." },
      { title: "Jumlah Donasi", description: "Nominal bantuan." },
      { title: "Aksi", description: "Tombol Edit dan Hapus." },
    ],
    filters: [
      { title: "Pagination", description: "Tombol Sebelumnya dan Berikutnya untuk berpindah halaman." },
    ],
    formFields: [
      { title: "Nama", description: "Nama penerima bantuan. Wajib diisi." },
      { title: "NIM", description: "NIM penerima bantuan. Wajib diisi." },
      { title: "Program Studi", description: "Program studi penerima. Wajib diisi." },
      { title: "Jenis Bantuan", description: "Pilihan: Biaya UKT, Biaya Hidup, Biaya Tugas Akhir, Biaya Kesehatan (Bagi yang membutuhkan dan bersifat darurat), Bantuan Orang Tua Asuh, Bantuan Lainnya." },
      { title: "Bulan", description: "Pilihan: Januari, Febuari, Maret, April, Mei, Juni, Juli, Agustus, September, Oktober, November, Desember." },
      { title: "Tahun", description: "Tahun bantuan. Wajib diisi." },
      { title: "Jumlah Donasi", description: "Nominal bantuan. Wajib diisi." },
    ],
    buttons: [
      { title: "Tambah", description: "Membuka form tambah dana bantuan." },
      { title: "Edit", description: "Membuka form edit dana bantuan." },
      { title: "Hapus", description: "Menghapus data dana bantuan setelah konfirmasi." },
      { title: "Cancel", description: "Menutup modal tanpa menyimpan." },
      { title: "Submit", description: "Menyimpan data dana bantuan." },
      { title: "Sebelumnya / Berikutnya", description: "Berpindah halaman data." },
    ],
    statusValues: [
      { title: "Biaya UKT", description: "Jenis bantuan untuk UKT." },
      { title: "Biaya Hidup", description: "Jenis bantuan untuk biaya hidup." },
      { title: "Biaya Tugas Akhir", description: "Jenis bantuan untuk tugas akhir." },
      { title: "Biaya Kesehatan (Bagi yang membutuhkan dan bersifat darurat)", description: "Jenis bantuan kesehatan darurat." },
      { title: "Bantuan Orang Tua Asuh", description: "Jenis bantuan program OTA." },
      { title: "Bantuan Lainnya", description: "Jenis bantuan di luar opsi utama." },
    ],
    steps: [
      { title: "Buka menu Dana Bantuan", description: "Klik menu Dana Bantuan pada sidebar." },
      { title: "Tambah data", description: "Klik Tambah, isi Nama, NIM, Program Studi, Jenis Bantuan, Bulan, Tahun, dan Jumlah Donasi." },
      { title: "Simpan data", description: "Klik Submit untuk menyimpan data." },
      { title: "Edit data", description: "Klik Edit pada baris data yang ingin diperbarui." },
      { title: "Hapus data", description: "Klik Hapus dan konfirmasi penghapusan." },
    ],
  },
  {
    id: "kemitraan",
    title: "Kemitraan",
    category: "Kemitraan",
    route: "/kemitraan",
    summary:
      "Menu Kemitraan digunakan untuk mengelola data mitra IOM ITB, termasuk nama mitra, PIC, nomor telepon PIC, deskripsi, logo, dan dokumen MoU PDF.",
    tableColumns: [
      { title: "Logo", description: "Logo mitra." },
      { title: "Nama Mitra", description: "Nama organisasi/perusahaan mitra." },
      { title: "Nama PIC", description: "Nama penanggung jawab mitra." },
      { title: "Nomor Telepon PIC", description: "Nomor telepon PIC." },
      { title: "Deskripsi", description: "Deskripsi singkat mitra." },
      { title: "MoU", description: "Dokumen MoU dalam format PDF." },
      { title: "Aksi", description: "Tombol Edit dan Hapus." },
    ],
    filters: [
      { title: "Pencarian", description: "Mencari data kemitraan pada halaman kemitraan." },
    ],
    formFields: [
      { title: "Nama Mitra", description: "Input nama mitra dengan placeholder mis. PT Example." },
      { title: "Nama PIC", description: "Input nama PIC dengan placeholder mis. Budi Santoso." },
      { title: "Nomor Telepon PIC", description: "Input nomor telepon dengan placeholder mis. 08123456789." },
      { title: "Deskripsi", description: "Textarea dengan placeholder Deskripsi singkat tentang mitra." },
      { title: "Logo", description: "Upload logo mitra." },
      { title: "MoU (PDF)", description: "Upload dokumen MoU dalam format PDF." },
    ],
    buttons: [
      { title: "Tambah Kemitraan", description: "Membuka modal tambah kemitraan." },
      { title: "Edit", description: "Membuka modal edit kemitraan." },
      { title: "Hapus", description: "Menghapus data kemitraan setelah konfirmasi." },
      { title: "Batal", description: "Menutup modal tanpa menyimpan." },
      { title: "Simpan", description: "Menyimpan data kemitraan." },
      { title: "Tombol X", description: "Menutup modal form." },
    ],
    statusValues: [],
    steps: [
      { title: "Buka menu Kemitraan", description: "Klik menu Kemitraan pada sidebar." },
      { title: "Tambah mitra", description: "Klik Tambah Kemitraan, isi Nama Mitra, Nama PIC, Nomor Telepon PIC, Deskripsi, Logo, dan MoU PDF." },
      { title: "Simpan data", description: "Klik Simpan untuk menyimpan data mitra." },
      { title: "Edit data", description: "Klik Edit pada baris mitra yang ingin diperbarui." },
      { title: "Hapus data", description: "Klik Hapus dan konfirmasi penghapusan." },
    ],
  },
  {
    id: "kegiatan-kemitraan",
    title: "Kegiatan Kemitraan",
    category: "Kemitraan",
    route: "/kegiatan-kemitraan",
    summary:
      "Menu Kegiatan Kemitraan digunakan untuk menghubungkan kegiatan dengan mitra, lokasi, periode, status, dan gambar publikasi.",
    tableColumns: [
      { title: "Mitra", description: "Nama mitra yang terhubung dengan kegiatan." },
      { title: "Nama Kegiatan", description: "Nama kegiatan kemitraan." },
      { title: "Deskripsi", description: "Deskripsi kegiatan." },
      { title: "Lokasi", description: "Lokasi kegiatan." },
      { title: "Status", description: "planned, ongoing, completed, atau cancelled." },
      { title: "Tanggal Mulai", description: "Tanggal mulai kegiatan." },
      { title: "Tanggal Selesai", description: "Tanggal selesai kegiatan." },
      { title: "Gambar", description: "Gambar kegiatan." },
      { title: "Aksi", description: "Tombol Edit dan Hapus." },
    ],
    filters: [
      { title: "Pencarian", description: "Mencari kegiatan kemitraan pada halaman kegiatan kemitraan." },
    ],
    formFields: [
      { title: "Mitra", description: "Select mitra dari data Kemitraan." },
      { title: "Nama Kegiatan", description: "Nama kegiatan. Wajib diisi." },
      { title: "Deskripsi", description: "Deskripsi kegiatan." },
      { title: "Lokasi", description: "Lokasi kegiatan." },
      { title: "Status", description: "Pilihan: planned, ongoing, completed, cancelled." },
      { title: "Tanggal Mulai", description: "Tanggal mulai kegiatan." },
      { title: "Tanggal Selesai", description: "Tanggal selesai kegiatan." },
      { title: "Gambar", description: "Upload gambar kegiatan." },
    ],
    buttons: [
      { title: "Tambah Kegiatan Kemitraan", description: "Membuka modal tambah kegiatan kemitraan." },
      { title: "Edit", description: "Membuka modal edit kegiatan kemitraan." },
      { title: "Hapus", description: "Menghapus kegiatan kemitraan setelah konfirmasi." },
      { title: "Batal", description: "Menutup modal tanpa menyimpan." },
      { title: "Simpan", description: "Menyimpan data kegiatan kemitraan." },
      { title: "Tombol X", description: "Menutup modal form." },
    ],
    statusValues: [
      { title: "planned", description: "Kegiatan direncanakan." },
      { title: "ongoing", description: "Kegiatan sedang berjalan." },
      { title: "completed", description: "Kegiatan selesai." },
      { title: "cancelled", description: "Kegiatan dibatalkan." },
    ],
    steps: [
      { title: "Buka menu Kegiatan Kemitraan", description: "Klik menu Kegiatan Kemitraan pada sidebar." },
      { title: "Tambah kegiatan", description: "Klik Tambah Kegiatan Kemitraan, pilih Mitra, isi Nama Kegiatan, Deskripsi, Lokasi, Status, Tanggal Mulai, Tanggal Selesai, dan Gambar." },
      { title: "Simpan data", description: "Klik Simpan untuk menyimpan kegiatan." },
      { title: "Edit data", description: "Klik Edit pada baris kegiatan yang ingin diperbarui." },
      { title: "Hapus data", description: "Klik Hapus dan konfirmasi penghapusan." },
    ],
  },
  {
    id: "dashboard-merchandise",
    title: "Dashboard Merchandise",
    category: "Produk",
    route: "/merchandise-dashboard",
    summary:
      "Dashboard Penjualan Merchandise menampilkan kinerja penjualan dari transaksi yang berhasil: jumlah terjual, omzet, HPP, laba kotor, dan margin, per kategori dan per produk.",
    tableColumns: [
      { title: "Kartu ringkasan", description: "Total Produk, Total Terjual, Omzet, Total HPP, Laba Kotor, dan Margin." },
      { title: "Omzet dan Laba Kotor per Kategori", description: "Grafik perbandingan antar kategori." },
      { title: "Top 10 Merchandise Terlaris", description: "Nama, Kategori, Harga Jual, HPP/Unit, dan jumlah Terjual." },
    ],
    filters: [
      { title: "Filter Kategori", description: "Batasi perhitungan ke satu kategori." },
    ],
    formFields: [],
    buttons: [],
    statusValues: [
      { title: "Omzet", description: "Dihitung per produk dari harga satuan saat dipesan (termasuk tambahan harga opsi), tanpa ongkir." },
      { title: "Laba Kotor / Margin", description: "Omzet dikurangi HPP; margin = laba kotor ÷ omzet. Isi HPP di menu Merchandise agar akurat." },
    ],
    steps: [
      { title: "Buka Dashboard Merchandise", description: "Klik Dashboard Merchandise pada sidebar." },
      { title: "Pilih kategori", description: "Gunakan Filter Kategori bila ingin melihat satu kategori." },
      { title: "Baca kinerja", description: "Periksa kartu ringkasan, grafik per kategori, dan daftar produk terlaris." },
    ],
  },
  {
    id: "template-pesan",
    title: "Template Pesan",
    category: "Komunikasi",
    route: "/template-pesan",
    summary:
      "Menu Template Pesan digunakan untuk mengubah isi email dan WhatsApp yang dikirim otomatis oleh sistem (konfirmasi donasi, pembayaran merchandise, status pengajuan, dan lainnya). Bendahara hanya melihat template yang berkaitan dengan donasi dan pembayaran.",
    tableColumns: [
      { title: "Daftar Template", description: "Nama template, kanal (Email/WhatsApp), dan status Aktif/Nonaktif." },
      { title: "Editor", description: "Subject Email (khusus email) dan isi pesan." },
      { title: "Variable yang dapat digunakan", description: "Daftar variabel, mis. {{name}} atau {{items}}, yang diganti otomatis dengan data asli saat dikirim." },
    ],
    filters: [
      { title: "Tab kanal", description: "Semua, Email, atau WhatsApp." },
    ],
    formFields: [
      { title: "Subject Email", description: "Judul email (hanya template email)." },
      { title: "Isi pesan", description: "Teks pesan. Variabel yang dikenali ditandai biru; klik nama variabel untuk menyisipkannya." },
    ],
    buttons: [
      { title: "Reset", description: "Membatalkan perubahan yang belum disimpan." },
      { title: "Preview & Test", description: "Melihat perkiraan tampilan pesan dengan contoh nilai variabel, lalu mengirim pesan uji ke nomor WhatsApp atau email Anda." },
      { title: "Simpan Template", description: "Menyimpan template. Pesan berikutnya memakai isi baru." },
    ],
    statusValues: [
      { title: "Aktif", description: "Template dipakai saat sistem mengirim pesan." },
      { title: "Nonaktif", description: "Template tidak dipakai." },
    ],
    steps: [
      { title: "Buka Template Pesan", description: "Klik Template Pesan pada sidebar (grup Komunikasi)." },
      { title: "Pilih template", description: "Gunakan tab Email/WhatsApp, lalu klik template yang ingin diubah." },
      { title: "Ubah isi", description: "Edit teks; sisipkan variabel dengan mengklik namanya. Jangan mengetik ulang nama variabel secara manual agar tidak salah eja." },
      { title: "Uji dulu", description: "Klik Preview & Test, isi contoh variabel, lalu kirim uji ke nomor/email sendiri." },
      { title: "Simpan", description: "Klik Simpan Template." },
    ],
  },
  {
    id: "broadcast",
    title: "Broadcast Pesan",
    category: "Komunikasi",
    route: "/broadcast",
    summary:
      "Menu Broadcast Pesan digunakan untuk mengirim pesan massal (pengingat iuran/donasi) via WhatsApp dan Email, baik terjadwal otomatis maupun dikirim sekarang, ke daftar penerima default.",
    tableColumns: [
      { title: "Tab Pengaturan", description: "Daftar pengaturan broadcast: Jenis Iuran, Interval, Jadwal, Status, Terakhir Dikirim, dan Aksi." },
      { title: "Tab Penerima", description: "Daftar Penerima Default: Nama, NIM/Relasi, No. WhatsApp, Email." },
      { title: "Tab Log Pengiriman", description: "Riwayat pengiriman: Waktu, Broadcast, status Kanal WhatsApp dan Kanal Email (arahkan kursor untuk melihat pesan error)." },
    ],
    filters: [],
    formFields: [
      { title: "Nama Pengaturan", description: "Judul internal, mis. Pengingat Iuran Wajib Bulanan." },
      { title: "Jenis Iuran", description: "Mengisi variabel {{jenisIuran}} pada pesan." },
      { title: "Jadwal Pengiriman", description: "Interval Mingguan (hari ke-1–7), Bulanan, atau 3 Bulanan (tanggal 1–31), beserta jam." },
      { title: "Template Pesan", description: "Isi pesan; gunakan {{name}} dan {{jenisIuran}}." },
      { title: "Status Aktif / Jadwal Aktif", description: "Pengaturan dan jadwal otomatis hanya berjalan bila aktif." },
      { title: "Penerima", description: "Nama, NIM/Relasi, No. WhatsApp (format 628…), Email. Minimal salah satu WhatsApp atau Email. Bisa impor CSV/XLS/XLSX maksimal 5 MB." },
    ],
    buttons: [
      { title: "Tambah Pengaturan", description: "Membuat pengaturan broadcast baru." },
      { title: "Blast (Kirim Sekarang)", description: "Mengirim broadcast saat itu juga ke semua penerima." },
      { title: "Edit / Hapus", description: "Mengubah atau menghapus pengaturan atau penerima." },
      { title: "Tambah Penerima", description: "Menambah penerima manual atau Impor CSV/XLS." },
    ],
    statusValues: [
      { title: "Mingguan / Bulanan / 3 Bulan", description: "Interval pengiriman otomatis." },
      { title: "Aktif / Nonaktif", description: "Status pengaturan broadcast." },
    ],
    steps: [
      { title: "Siapkan penerima", description: "Di tab Penerima klik Tambah Penerima, isi manual atau impor file CSV/XLS." },
      { title: "Buat pengaturan", description: "Di tab Pengaturan klik Tambah Pengaturan, isi nama, jenis iuran, jadwal, dan template pesan." },
      { title: "Aktifkan", description: "Centang Status Aktif dan Jadwal Aktif bila ingin dikirim otomatis, lalu Simpan Pengaturan." },
      { title: "Kirim manual bila perlu", description: "Klik Blast untuk mengirim sekarang. Pesan massal tidak bisa ditarik kembali — periksa isi dan penerima dulu." },
      { title: "Pantau hasil", description: "Buka Log Pengiriman untuk melihat pesan yang berhasil atau gagal." },
    ],
  },
];

export const MANUAL_ENTRIES: ManualEntry[] = [...GENERAL_DOCS, ...FEATURE_DOCS];
